import { Component } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { TranslocoService } from '@ngneat/transloco';
import { map, mergeMap, Observable, of } from 'rxjs';
import { filter } from 'rxjs/operators';

import { LoanApplicationService } from 'src/app/core/services/loan-application/loan-application.service';
import { SessionStorageService } from 'src/app/core/services/storage/session-storage.service';

import { RoutingPathsEnum, RoutingService } from 'src/app/core/services/routing/routing.service';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { TagDataService } from 'src/app/core/services/tag-data/tag-data.service';
import { AddressTypeEnum, IAchBankAccountResult, MobileApiService } from 'src/app/core/services/mobile-api';
import { ConfigApiService } from 'src/app/core/services/mobile-api/config-api/config-api.service';
import {
	DebitCardErrorDialogComponent,
	DebitCardErrorDialogData
} from 'src/app/shared/components/dialogs/debit-card-error-dialog/debit-card-error-dialog.component';
import { AddDebitCardComponent } from './add-debit-card.component';
import { DisbursementType } from 'src/app/core/services/loan-application/disbursement/disbursement.model';
import { ConfirmTermsService } from 'src/app/terms/confirm-terms.service';

@Component({
	selector: 'op-add-debit-card-post-approval',
	templateUrl: './add-debit-card-post-approval.component.html',
	styleUrls: ['./add-debit-card.component.scss']
})
export class AddDebitCardPostApprovalComponent extends AddDebitCardComponent {
	protected nextRoute: RoutingPathsEnum = RoutingPathsEnum.esignAutoPay;

	protected backRoute: RoutingPathsEnum = RoutingPathsEnum.expediteDisbursement;

	cardErrorNote: string | null = null;

	highlightMessage: boolean = true;

	constructor(
		protected sanitizer: DomSanitizer,
		protected translocoService: TranslocoService,
		protected tagDataService: TagDataService,
		protected routingService: RoutingService,
		protected configService: ConfigApiService,
		protected dialogService: DialogService,
		protected loanAppService: LoanApplicationService,
		protected mobileApiService: MobileApiService,
		protected sessionStorageService: SessionStorageService,
		private confirmTermsService: ConfirmTermsService
	) {
		super(
			sanitizer,
			translocoService,
			tagDataService,
			routingService,
			configService,
			dialogService,
			loanAppService,
			mobileApiService,
			sessionStorageService
		);
	}

	ngOnInit() {
		const routingSubscription = this.confirmTermsService.getNextRoute().subscribe((nextRoute) => {
			this.nextRoute = nextRoute;
			this.backRoute = nextRoute;
			super.ngOnInit();
		});
		this.subscription.add(routingSubscription);
	}

	handleDebitCardError() {
		this.getDebitCardErrorDialogData()
			.pipe(
				filter((debiCardErrorData) => Boolean(debiCardErrorData)),
				mergeMap((debiCardErrorData: DebitCardErrorDialogData) => {
					return this.dialogService.openDialog(
						DebitCardErrorDialogComponent,
						{ data: debiCardErrorData },
						() => of(),
						() => of()
					);
				})
			)
			.subscribe({
				complete: () => {
					this.routingService.route(this.nextRoute);
				}
			});
	}

	setDebitCardError(message: string | null): void {
		if (message) {
			this.cardErrorMessage = `DISBURSEMENT_SELECTION.DEBIT_CARD.SWITCH.retry`;
			this.cardErrorNote = `DISBURSEMENT_SELECTION.DEBIT_CARD.SWITCH.note`;
			this.highlightMessage = true;
		} else {
			this.cardErrorMessage = null;
			this.cardErrorNote = null;
		}
	}

	getDebitCardErrorDialogData(): Observable<DebitCardErrorDialogData | null> {
		const disbursementType = this.loanApp?.disbursement?.disbursementType;
		if (disbursementType === DisbursementType.ach) {
			return this.mobileApiService.getAchBankAccount(this.loanApp.id).pipe(
				map((achBankAccount: IAchBankAccountResult) => {
					return {
						disbursementType: disbursementType,
						accountLast4: achBankAccount?.accountNumber
					};
				})
			);
		} else if (disbursementType === DisbursementType.check) {
			return this.mobileApiService.getAddresses(this.loanApp.id).pipe(
				map((addresses) => {
					return addresses?.find((address) => address?.type === AddressTypeEnum.mail);
				}),
				filter((address) => Boolean(address)),
				map((address) => {
					const addressText = `${address?.streetAddress1 || ''} ${address?.streetAddress2 || ''} ${address?.city}, ${
						address?.state
					} ${address?.postalCode}`;
					return {
						disbursementType: disbursementType,
						address: addressText?.trim()
					};
				})
			);
		} else {
			return of(null);
		}
	}

	refreshNextRoute(): Observable<RoutingPathsEnum> {
		return this.confirmTermsService.getNextRoute();
	}
}
